import type {
  Aggregate,
  CogniteClient,
  DatapointAggregates,
  Datapoints,
  DatapointsQuery,
  Timestamp,
} from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';

import { chunk } from '#core/lib/utils/array.ts';

import { CDF_DATAPOINTS_BATCH_SIZE } from './cdf-constants.ts';

/**
 * Spec describing a datapoints retrieval window/aggregation.
 *
 * Mirrors the relevant fields of the SDK's `DatapointsMultiQuery` so this
 * module can act as a 1:1 façade for `client.datapoints.retrieve` with cache
 * dedup + batch chunking baked in. Pass `aggregates`/`granularity` for
 * server-side aggregation; omit them for raw datapoints.
 */
export interface DatapointsFetchSpec {
  /** Inclusive lower bound. SDK accepts `Date | number | "Nx-ago" | "now"`. */
  start?: string | Timestamp;
  /** Inclusive upper bound. SDK accepts the same forms as `start`. */
  end?: string | Timestamp;
  /** Granularity string (e.g. `'1h'`, `'1d'`, `'7d'`, `'30d'`). */
  granularity?: string;
  /** Aggregate functions to compute per granularity bucket. Omit for raw points. */
  aggregates?: Aggregate[];
  /** Max points per series in the response. */
  limit?: number;
}

/**
 * Single-item shape accepted by `client.datapoints.retrieve` — `id`,
 * `externalId`, or `instanceId` discriminates the lookup form.
 */
export type DatapointsBatchItem = DatapointsQuery;

/**
 * Result row returned by `client.datapoints.retrieve` — either raw points
 * (no `aggregates`) or aggregate buckets. Both share the metadata shape
 * we key off (`externalId`, `instanceId`).
 */
export type DatapointsBatchResult = Datapoints | DatapointAggregates;

interface FetchDatapointsBatchedOptions {
  client: CogniteClient;
  queryClient: QueryClient;
  items: readonly DatapointsBatchItem[];
  spec?: DatapointsFetchSpec;
  /** TanStack Query stale time in ms. Defaults to 60s. */
  staleTime?: number;
}

const DEFAULT_STALE_TIME_MS = 60_000;

/**
 * Canonical identity string for a single fetch item. Used to:
 *  - sort items into a stable order (so identical input sets hash to the
 *    same query key regardless of caller-side iteration order),
 *  - serve as the lookup key when assembling results into a `Map`.
 *
 * Lookup form is preserved in the prefix so an `externalId: "x"` request
 * and an `instanceId: { space: "s", externalId: "x" }` request — which
 * may resolve to the same series server-side but are distinct API calls
 * — get distinct cache entries. The SDK can return different responses
 * for the two forms (e.g. when a series has no `externalId` set).
 */
function getItemKey(item: DatapointsBatchItem): string {
  if ('instanceId' in item && item.instanceId) {
    return `instance:${item.instanceId.space}/${item.instanceId.externalId}`;
  }
  if ('externalId' in item && item.externalId !== undefined) {
    return `ext:${item.externalId}`;
  }
  if ('id' in item && item.id !== undefined) {
    return `id:${item.id}`;
  }
  return 'unknown';
}

/**
 * Pick the externalId to key the result map under. Prefers the top-level
 * `externalId` populated by the SDK, falling back to `instanceId.externalId`
 * for instance-id queries (mirrors the existing behaviour in
 * `useTimeSeriesDatapoints` and `enrichTimeseriesRefs`).
 */
function getResultKey(result: DatapointsBatchResult): string | undefined {
  if (result.externalId !== undefined) {
    return result.externalId;
  }
  const withInstance = result as { instanceId?: { externalId?: string } };
  return withInstance.instanceId?.externalId;
}
/**
 * Assemble a flat sequence of datapoints results into a `Map` keyed by
 * externalId (with `instanceId.externalId` fallback via {@link getResultKey}).
 *
 * Shared between {@link fetchDatapointsBatched} (async producer-context
 * path) and `useTimeSeriesDatapoints` (React-render path) so both produce
 * identically-shaped output. Series whose key cannot be resolved are
 * dropped — `ignoreUnknownIds: true` on the SDK side already filters
 * unknown series, so this only affects the malformed-response edge case.
 */
export function assembleDatapointsMap(series: Iterable<DatapointsBatchResult>): Map<string, DatapointsBatchResult> {
  const map = new Map<string, DatapointsBatchResult>();
  for (const s of series) {
    const key = getResultKey(s);
    if (key !== undefined) {
      map.set(key, s);
    }
  }
  return map;
}
/**
 * Single SDK call wrapper used by both the async kernel and the React
 * hook's `queryFn`. Centralises the `items` / `spec` / `ignoreUnknownIds`
 * call shape and the response narrowing so neither call site repeats
 * the SDK boilerplate.
 *
 * Accepts `client: CogniteClient | undefined` so the React hook — whose
 * `useCogniteClient` may transiently return `undefined` during init —
 * can invoke this without a separate null-check; in that case the call
 * short-circuits to an empty result list (preserves the existing
 * `client?.datapoints.retrieve(...)` behaviour from before this refactor).
 */
export async function retrieveDatapointsBatch(
  client: CogniteClient | undefined,
  batch: readonly DatapointsBatchItem[],
  spec: DatapointsFetchSpec | undefined,
): Promise<DatapointsBatchResult[]> {
  const result = await client?.datapoints.retrieve({
    items: [...batch],
    ...spec,
    ignoreUnknownIds: true,
  });
  return (result ?? []) as DatapointsBatchResult[];
}

/**
 * Canonical query-key factory for batched datapoints fetches.
 *
 * The key depends only on the canonical identity of each item (via
 * `getItemKey`, sorted) and the spec, so two callers asking for the same
 * series + window hit the same TanStack Query cache entry regardless of
 * request order or surrounding caller. Exported so call sites that build
 * their own `useSuspenseQueries` / `fetchQuery` shells (notably the
 * `useTimeSeriesDatapoints` hook) can produce the same key the async
 * function uses, enabling cross-context cache reuse.
 */
export function datapointsBatchedQueryKey(
  items: readonly DatapointsBatchItem[],
  spec: DatapointsFetchSpec | undefined,
): readonly unknown[] {
  return [
    'datapointsBatched',
    {
      items: items.map(getItemKey).sort(),
      spec: spec ?? {},
    },
  ];
}

/**
 * Batched datapoints fetch with TanStack Query cache dedup.
 *
 * Used by:
 *  - `useTimeSeriesDatapoints` (React render context, via a render-side
 *    `useSuspenseQueries` shell that reuses {@link datapointsBatchedQueryKey})
 *  - `fetchTimeseriesStep` (chat: wide chart-format dataset)
 *  - `enrichTimeseriesRefs` (chat: per-row ref enrichment)
 *
 * Chunks `items` by {@link CDF_DATAPOINTS_BATCH_SIZE}, fetches each chunk
 * in parallel through the provided `queryClient`, and assembles results
 * into a `Map` keyed by externalId. Series absent from the map are either
 * unknown to CDF (filtered server-side via `ignoreUnknownIds: true`) or
 * returned no datapoints in the requested window.
 *
 * Items are sorted by {@link getItemKey} before chunking so identical
 * input sets — across callers and across iterations — produce identical
 * cache keys.
 */
export async function fetchDatapointsBatched({
  client,
  queryClient,
  items,
  spec,
  staleTime = DEFAULT_STALE_TIME_MS,
}: FetchDatapointsBatchedOptions): Promise<Map<string, DatapointsBatchResult>> {
  if (items.length === 0) {
    return new Map();
  }

  const sortedItems = [...items].sort((a, b) => getItemKey(a).localeCompare(getItemKey(b)));
  const batches = chunk(sortedItems, CDF_DATAPOINTS_BATCH_SIZE);

  const batchResults = await Promise.all(
    batches.map((batch) =>
      queryClient.fetchQuery({
        queryKey: datapointsBatchedQueryKey(batch, spec),
        queryFn: () => retrieveDatapointsBatch(client, batch, spec),
        staleTime,
      }),
    ),
  );

  return assembleDatapointsMap(batchResults.flat());
}
